// Pagina Clienti /clienti — lista aggregata con ricerca debounced + paginazione
// "Carica altri". Righe cliccabili → scheda cliente /clienti/:key.
import { useEffect, useRef, useState } from "react";
import { CampoCerca, ChipFiltro } from "@/shared/ui/ChipFiltro";
import type { StatoTone } from "@/features/ordini/components/statoToni";
import { Link } from "react-router-dom";
import { Card } from "@/shared/ui/Card";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { PageHeader } from "@/shared/ui/PageHeader";
import { formatMoney } from "@/shared/format";
import { prettifyApiError } from "@/shared/api";
import { usePersistedState } from "@/shared/usePersistedState";
import { fetchCustomers, formatDateIt } from "./api";
import { useDebounced } from "@/shared/useDebounced";
import type { CustomerListItem, CustomerSegment } from "./types";

const PAGE_SIZE = 50;

// Chip segmento (single-select): "tutti" = nessun filtro (segment null).
// Redesign 27/10: i chip della lista ordini, un tono per segmento.
const SEGMENTS: ReadonlyArray<{ id: "tutti" | CustomerSegment; label: string; tono: StatoTone }> = [
  { id: "tutti", label: "Tutti", tono: "annullato" },
  { id: "top", label: "Top", tono: "bonifico" },
  { id: "ricorrenti", label: "Ricorrenti", tono: "ordinato" },
  { id: "problemi", label: "Con problemi", tono: "da-pagare" },
];

export function ClientiPage() {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounced(query, 300);
  // Segmento persistito in sessionStorage: sopravvive alla navigazione fra le
  // pagine, riparte pulito a scheda nuova (vedi usePersistedState).
  const [segment, setSegment] = usePersistedState<CustomerSegment | null>("clienti.segment", null);

  const [items, setItems] = useState<CustomerListItem[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Contatore richieste: l'effetto prima-pagina lo incrementa a ogni cambio
  // filtri; loadMore lo campiona prima della fetch e lo riconfronta dopo l'await
  // per scartare una risposta stale (serve un ref, non uno snapshot di closure).
  const requestId = useRef(0);

  // Prima pagina: rifatta a ogni cambio ricerca (offset azzerato). Abort nel
  // cleanup per evitare setState su race di ricerche rapide / unmount.
  useEffect(() => {
    const ctrl = new AbortController();
    let active = true;
    requestId.current += 1;
    setIsLoading(true);
    setError(null);
    fetchCustomers({ q: debouncedQuery, limit: PAGE_SIZE, offset: 0, segment }, ctrl.signal)
      .then((rows) => {
        if (!active) return;
        setItems(rows);
        setOffset(rows.length);
        setHasMore(rows.length === PAGE_SIZE);
      })
      .catch((err) => {
        if (!active || ctrl.signal.aborted) return;
        setError(prettifyApiError(err));
        setItems([]);
        setHasMore(false);
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
      ctrl.abort();
    };
  }, [debouncedQuery, segment]);

  async function loadMore() {
    const reqAtStart = requestId.current;
    setIsLoadingMore(true);
    setError(null);
    try {
      const rows = await fetchCustomers({ q: debouncedQuery, limit: PAGE_SIZE, offset, segment });
      // Filtri cambiati durante la fetch (l'effetto prima-pagina ha rilanciato):
      // scarta il risultato stale, non mescolarlo alle righe dei nuovi filtri.
      if (reqAtStart !== requestId.current) return;
      setItems((prev) => [...prev, ...rows]);
      setOffset((o) => o + rows.length);
      setHasMore(rows.length === PAGE_SIZE);
    } catch (err) {
      if (reqAtStart !== requestId.current) return;
      setError(prettifyApiError(err));
    } finally {
      setIsLoadingMore(false);
    }
  }

  return (
    <main className="flex-1 overflow-y-auto px-7 pb-8 pt-5 scroll-paper max-md:px-3 max-md:pt-3">
      <PageHeader
        title="Clienti"
        subtitle={
          items.length > 0
            ? `${items.length}${hasMore ? "+" : ""} clienti`
            : "Clienti con almeno un ordine"
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2 max-md:flex-col max-md:items-stretch">
        <CampoCerca
          value={query}
          onChange={setQuery}
          placeholder="Cerca per nome, email o città…"
          ariaLabel="Cerca clienti"
          className="w-full max-w-[420px] max-md:max-w-none"
        />
        <div
          className="flex flex-wrap items-center gap-1 max-md:gap-2"
          role="group"
          aria-label="Filtra per segmento"
        >
          {SEGMENTS.map((s) => (
            <ChipFiltro
              key={s.id}
              tono={s.tono}
              attivo={s.id === "tutti" ? segment === null : segment === s.id}
              onClick={() => setSegment(s.id === "tutti" ? null : s.id)}
            >
              {s.label}
            </ChipFiltro>
          ))}
        </div>
      </div>

      <ErrorBanner message={error} className="mb-3" />

      {isLoading ? (
        <Card className="py-8 text-center">
          <p className="text-[12.5px] text-ink-2">Caricamento clienti…</p>
        </Card>
      ) : items.length === 0 ? (
        <Card className="py-8 text-center">
          <h3 className="mb-1 font-display text-[16px] font-bold text-ink">Nessun cliente</h3>
          <p className="text-[12px] text-ink-2">
            {debouncedQuery.trim()
              ? `Nessun cliente corrisponde a «${debouncedQuery.trim()}».`
              : "Non ci sono ancora clienti con ordini in archivio."}
          </p>
        </Card>
      ) : (
        <>
          <div aria-label="Lista clienti" className="flex flex-col gap-1.5">
            {items.map((c) => (
              <Link
                key={c.key}
                to={`/clienti/${encodeURIComponent(c.key)}`}
                className="flex items-center gap-4 rounded-xl border border-line bg-card px-4 py-3 shadow-[0_1px_2px_rgba(31,27,22,0.04)] transition-colors hover:border-ink-4 max-md:flex-wrap max-md:gap-x-4 max-md:gap-y-1"
              >
                <div className="min-w-0 flex-1 max-md:basis-full">
                  <div className="truncate text-[14px] font-bold text-ink">{c.name}</div>
                  <div className="truncate text-[12px] text-ink-2">
                    {[c.email, c.city].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div className="shrink-0 text-right text-[12.5px] text-ink">
                  {c.ordersCount} {c.ordersCount === 1 ? "ordine" : "ordini"}
                </div>
                <div className="w-[110px] shrink-0 text-right font-display text-[14px] font-semibold tabular-nums text-ink">
                  {formatMoney(c.ltv, c.ltvCurrency)}
                </div>
                <div className="w-[84px] shrink-0 text-right text-[12.5px] tabular-nums text-ink">
                  {formatDateIt(c.lastOrderAt) || "—"}
                </div>
              </Link>
            ))}
          </div>

          {hasMore && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={isLoadingMore}
                className="h-[34px] rounded-full border border-line-2 bg-card px-4 text-[12.5px] font-semibold text-ink transition-colors hover:border-ink-4 disabled:opacity-50"
              >
                {isLoadingMore ? "Carico…" : "Carica altri"}
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}
